import type { FccSeatStatusType } from '@backoffice-frontend/graphql/subscription'

import { useTheme } from '@backoffice-frontend/patterns'
import {
  SeatbeltClosedIcon,
  SeatbeltIcon,
  SteeringWheelIcon,
  SuitcaseIcon,
} from '@moia-dev/pace-icons'
import type { HTMLAttributes } from 'react'

const Indicator = ({ children, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div
    css={theme => ({
      borderRadius: theme.spacing(0.5),
      border: `${theme.spacing(0.25)} solid ${
        theme.semantic.ColorAccessoryDefault
      }`,
      backgroundColor: theme.semantic.ColorAccessoryReducedAlternative,
      height: 32,
      width: 32,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(0.5),
      transitionProperty: 'border',
      transitionDuration: '0.3s',
      transitionTimingFunction: 'ease-in-out',
    })}
    {...rest}
  >
    {children}
  </div>
)

type AdSeatIndicatorProps = {
  type?: FccSeatStatusType | 'LUGGAGE' | 'DRIVER'
}

export const AdSeatIndicator = ({ type }: AdSeatIndicatorProps) => {
  const theme = useTheme()

  switch (type) {
    case 'NOT_OCCUPIED':
      return (
        <Indicator>
          <div
            css={{
              height: 1,
              width: '100%',
              backgroundColor: theme.semantic.ColorAccessoryDefault,
              transform: 'rotate(45deg)',
            }}
          />
        </Indicator>
      )
    case 'LUGGAGE':
      return (
        <Indicator>
          <SuitcaseIcon color={theme.semantic.ColorAccessoryDefault} />
        </Indicator>
      )
    case 'DRIVER':
      return (
        <Indicator>
          <SteeringWheelIcon color={theme.semantic.ColorAccessoryDefault} />
        </Indicator>
      )
    case 'BUCKLED':
      return (
        <Indicator
          aria-label="seat buckled"
          css={{
            borderColor: theme.semantic.ColorSuccessDefault,
            backgroundColor: theme.semantic.ColorSurfaceSuccess,
          }}
        >
          <SeatbeltClosedIcon color={theme.semantic.ColorSuccessDefault} />
        </Indicator>
      )
    case 'UNBUCKLED':
      return (
        <Indicator
          aria-label="seat unbuckled"
          css={{
            borderColor: theme.semantic.ColorCriticalDefault,
            backgroundColor: theme.semantic.ColorSurfaceCritical,
          }}
        >
          <SeatbeltIcon color={theme.semantic.ColorCriticalDefault} />
        </Indicator>
      )
    default:
      return (
        // biome-ignore lint/a11y/useAriaPropsSupportedByRole: <auto ignored after biome migration>
        <div aria-label="seat placeholder" css={{ height: 32, width: 32 }} />
      )
  }
}
